<template>
  <main
    class="container"
    :class="{
      'container--wide': productOptionsCount === 3,
      'container--really-wide': productOptionsCount > 3,
    }"
  >
    <PackageProgress
      v-if="!isSingleSelectMode"
      :current-product-index="productIndex"
    />

    <ProductPicker
      :product="product"
      :discount="discount"
      :key="product.type"
      @price-changed="updatePrice"
    />

    <button
      class="btn btn--big next-btn"
      @click="trackSubmitBtn"
      data-testid="package-builder-next"
    >
      {{ nextRoute.text }}
    </button>

    <CurvedBar direction="bottom" curveFactorPc="30"></CurvedBar>
  </main>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import CurvedBar from "@/components/CurvedBar";
import PackageProgress from "@/components/PackageProgress";
import ProductPicker from "@/components/ProductPicker";

export default {
  name: "PackageBuilder",
  computed: {
    ...mapState(["agent"]),
    isSingleSelectMode() {
      return this.agent.isValid;
    },
    products() {
      return this.$store.state.products;
    },
    productIndex() {
      return this.products.findIndex(
        (product) => product.type === this.$route.params.type,
      );
    },
    product() {
      return this.products[this.productIndex];
    },
    productOptionsCount() {
      return this.product.options.length;
    },
    pricePerPerson() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    pickedProductPrices() {
      return this.$store.state.pickedProductPrices;
    },
    pricePerPersonForChosen() {
      return (
        this.pickedProductPrices.reduce((sum, price) => sum + price, 0) / 100
      ).toFixed(2);
    },
    discount() {
      const { discount } = this.$store.state;

      if (
        this.$store.getters.hasValidDiscount &&
        discount.productType === this.product.type &&
        this.product.options.some(
          (option) => discount.productCode === option.code,
        )
      ) {
        return discount;
      }

      return null;
    },
    nextRoute() {
      const products = this.products;
      const route = {};

      if (!this.isSingleSelectMode && this.productIndex < products.length - 1) {
        route.url = products[this.productIndex + 1].type;
        route.text = "Next";
        route.eventTrackingLabelText = `Select ${
          products[this.productIndex + 1].label
        }`;
      } else {
        route.url = "/short-summary";
        route.text = "Next";
        route.eventTrackingLabelText = "Your Price";
      }

      return route;
    },
  },
  methods: {
    ...mapActions(["updatePreSignupLeadProductData"]),
    ...mapMutations({
      showBackBtn: "SHOW_BACK_BTN",
      setPickedProductPrice: "SET_PICKED_PRODUCT_PRICE",
    }),
    trackSubmitBtn() {
      if (this.productIndex < this.products.length - 1) {
        this.trackGoogleEvent(
          `Click Next on ${this.product.label} Selection`,
          "Get Price",
          this.nextRoute.eventTrackingLabelText,
        );

        this.trackFacebookEvent(
          `Click Next on ${this.product.label} Selection`,
        );
      } else {
        this.trackGoogleEvent(
          `Click ${this.nextRoute.eventTrackingLabelText} Button`,
          "Get Price",
          this.nextRoute.eventTrackingLabelText,
        );

        this.trackFacebookEvent(
          `Click ${this.nextRoute.eventTrackingLabelText} Button`,
        );
      }

      const option = this.product.options.find((option) => {
        return option.code === this.product.selected;
      });

      this.updatePreSignupLeadProductData(option.priceId);

      this.$router.push(this.nextRoute.url);
    },
    updatePrice(price) {
      this.setPickedProductPrice({
        index: this.productIndex,
        price: price,
      });
    },
  },
  created() {
    this.showBackBtn();
  },
  components: {
    CurvedBar,
    PackageProgress,
    ProductPicker,
  },
};
</script>

<style lang="scss" scoped>
.container {
  @include media(">=medium") {
    max-width: 80rem;
  }
}

.container--wide {
  @include media(">=medium") {
    max-width: 120rem;
  }
}

.container--really-wide {
  @include media(">=medium") {
    max-width: 95vw;
  }
}

.price-bubble {
  width: 17rem;
  height: 17rem;
  margin-top: -30px;

  background: $brand-gradient-reverse;
  box-shadow: $brand-box-shadow;
  border-radius: 50%;

  color: white;
  font-weight: lighter;
  text-align: center;

  z-index: 2;

  @include media("<medium") {
    width: 120px;
    height: 120px;
    margin-top: -10px;
  }

  .price-data {
    margin-top: 25px;
    line-height: 1.2em;

    @include media("<medium") {
      margin-top: 20px;
      line-height: 1em;
    }

    span {
      display: block;
    }

    .total-label {
      font-size: $subtitle-font-size;

      @include media("<medium") {
        font-size: 14px;
      }
    }

    .current-price {
      font-weight: 500;
      font-size: $title-font-size;

      @include media("<medium") {
        font-size: 24px;
      }
    }

    .pppw {
      font-size: $small-font-size;

      @include media("<medium") {
        font-size: 10px;
      }
    }
  }
}

.btn-wrapper {
  text-align: center;

  @include media("<medium") {
    display: none;
  }
}

.footer-next-btn {
  position: absolute;

  bottom: 10px;
  left: 50vw;
  width: 40%;
  max-width: 150px;

  text-indent: 40%;

  padding: $spacing-xs;
  border-radius: $border-radius-small;
  margin-bottom: 0;
  border: none;

  background: $brand-green;
  color: white;
  font-weight: $font-regular;

  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
  }

  @include media(">=medium") {
    display: none;
  }
}
</style>
